import { z } from "zod";
import { DEFAULT_TARGET_SECONDS, DEFAULT_TIMEZONE, isValidTimeZone } from "./time.js";
export const weekStartSchema = z.enum(["mon", "sun"]);
export const timezoneSchema = z
    .string()
    .min(1)
    .refine(isValidTimeZone, "Must be a valid IANA timezone identifier");
export const statsRangeSchema = z.enum(["this_week", "last_7d", "last_30d"]);
export const epochMsSchema = z.number().int().nonnegative();
export const targetSecondsSchema = z.number().int().min(60).max(30 * 24 * 60 * 60);
export const fastSchema = z.object({
    id: z.string().uuid(),
    start_ts: epochMsSchema,
    end_ts: epochMsSchema.nullable(),
    target_seconds: targetSecondsSchema,
    note: z.string().nullable(),
    created_at: epochMsSchema,
    updated_at: epochMsSchema
});
export const settingsSchema = z.object({
    default_target_seconds: targetSecondsSchema.default(DEFAULT_TARGET_SECONDS),
    timezone: timezoneSchema.default(DEFAULT_TIMEZONE),
    week_start: weekStartSchema.default("mon"),
    created_at: epochMsSchema,
    updated_at: epochMsSchema
});
export const startFastInputSchema = z.object({
    start_ts: epochMsSchema.optional(),
    target_seconds: targetSecondsSchema.optional(),
    note: z.string().max(2000).optional()
});
export const endFastInputSchema = z.object({
    end_ts: epochMsSchema.optional()
});
export const createFastInputSchema = z.object({
    start_ts: epochMsSchema,
    end_ts: epochMsSchema,
    target_seconds: targetSecondsSchema.optional(),
    note: z.string().max(2000).optional()
});
export const updateFastInputSchema = z
    .object({
    start_ts: epochMsSchema.optional(),
    end_ts: epochMsSchema.nullable().optional(),
    target_seconds: targetSecondsSchema.optional(),
    note: z.string().max(2000).nullable().optional()
})
    .refine((input) => Object.keys(input).length > 0, "At least one field is required");
export const updateSettingsInputSchema = z
    .object({
    default_target_seconds: targetSecondsSchema.optional(),
    timezone: timezoneSchema.optional(),
    week_start: weekStartSchema.optional()
})
    .refine((input) => Object.keys(input).length > 0, "At least one field is required");
export const currentFastResponseSchema = z.object({
    fast: fastSchema.nullable(),
    elapsed_seconds: z.number().int().nonnegative(),
    server_now_ts: epochMsSchema
});
export const statsResponseSchema = z.object({
    range: statsRangeSchema,
    from_date: z.string(),
    to_date: z.string(),
    count: z.number().int().nonnegative(),
    total_seconds: z.number().int().nonnegative(),
    average_seconds: z.number().int().nonnegative(),
    longest_seconds: z.number().int().nonnegative(),
    target_met_count: z.number().int().nonnegative(),
    target_met_percent: z.number().min(0).max(100),
    current_streak: z.number().int().nonnegative(),
    daily: z.array(z.object({
        date: z.string(),
        total_seconds: z.number().int().nonnegative(),
        target_seconds: targetSecondsSchema
    }))
});
export const usernameSchema = z
    .string()
    .trim()
    .min(2)
    .max(32)
    .regex(/^[a-z0-9][a-z0-9._-]*$/i, "Use letters, digits, dot, dash, or underscore");
export const passwordSchema = z.string().min(8).max(200);
export const userSchema = z.object({
    id: z.string().uuid(),
    username: z.string(),
    display_name: z.string(),
    created_at: epochMsSchema
});
export const loginInputSchema = z.object({
    username: usernameSchema,
    password: z.string().min(1)
});
export const deviceTokenInputSchema = z.object({
    username: usernameSchema,
    password: z.string().min(1),
    device_name: z.string().trim().min(1).max(64).optional()
});
export const changePasswordInputSchema = z.object({
    current_password: z.string().min(1),
    new_password: passwordSchema
});
export const sessionSchema = z.object({
    id: z.string().uuid(),
    kind: z.enum(["web", "device"]),
    label: z.string().nullable(),
    created_at: epochMsSchema,
    last_used_at: epochMsSchema,
    expires_at: epochMsSchema.nullable(),
    current: z.boolean()
});
